import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import consumerImage from "../assets/consumer.png";
import professionalImage from "../assets/professional.png";
import {
  LicenseIcon,
  SearchIcon,
  VerificationIcon,
} from "../assets/icons/icons";
import LandingFooter from "../components/LandingFooter";
import useFocusTrap from "../hooks/useFocusTrap";
import "./PublicSite.css";

const LOGO_WHITE_PUBLIC_PATH = "/clearbadge-logo-white.png";

const MISSION_POINTS = [
  {
    icon: VerificationIcon,
    title: "Trusted Identity Signals",
    description: "Build trust between consumers and licensed professionals.",
  },
  {
    icon: LicenseIcon,
    title: "Fraud And Spoof Prevention",
    description: "Reduce risk from impersonation, fake credentials, and profile spoofing.",
  },
  {
    icon: SearchIcon,
    iconClassName: "step-icon-fill",
    title: "Fast Verification Access",
    description: "Make verification fast, clear, and accessible before contact is made.",
  },
];

const CONSUMER_BENEFITS = [
  "Search by phone, email, or name and state to verify licensed professionals credentials in seconds.",
  "Find verified professionals in your area with confidence.",
  "Avoid high risk interactions with unknown or misrepresented identities.",
];

const PROFESSIONAL_BENEFITS = [
  "Prove who you are with a clear, portable verification profile.",
  "Reduce friction in sales conversations with instant credential proof.",
  "Stand out from unverified listings and build confidence faster.",
];

export default function AboutPage() {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingNav, setShowFloatingNav] = useState(true);
  const landingRootRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const mobileDrawerRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);
  const isVerifyPage = location.pathname === "/";
  const isFindProPage =
    location.pathname.startsWith("/find-a-pro") || location.pathname.startsWith("/find-pro");
  const isPricingPage = location.pathname.startsWith("/pricing");
  const isAboutPage = location.pathname.startsWith("/about");
  const isPrivacyPage = location.pathname.startsWith("/privacy-policy");
  const isTermsPage = location.pathname.startsWith("/terms-of-service");

  useFocusTrap({
    isOpen: mobileMenuOpen,
    containerRef: mobileDrawerRef,
    onClose: () => setMobileMenuOpen(false),
    returnFocusRef: mobileMenuButtonRef,
    lockScroll: true,
  });

  useEffect(() => {
    const rootNode = landingRootRef.current;
    if (!rootNode) return undefined;
    const revealNodes = rootNode.querySelectorAll("[data-reveal]");
    if (!revealNodes.length) return undefined;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      revealNodes.forEach((node) => node.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.16, rootMargin: "0px 0px -10% 0px" }
    );

    revealNodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [location.pathname]);

  useEffect(() => {
    lastScrollYRef.current = window.scrollY || 0;

    const onScroll = () => {
      const currentY = window.scrollY || 0;
      const previousY = lastScrollYRef.current;
      const delta = currentY - previousY;
      if (Math.abs(delta) < 6) return;

      if (currentY <= 24) {
        setShowFloatingNav(true);
      } else if (delta > 0) {
        setShowFloatingNav(false);
      } else {
        setShowFloatingNav(true);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) setShowFloatingNav(true);
  }, [mobileMenuOpen]);

  return (
    <div ref={landingRootRef} className="public-shell landing about-page clearbadge-public">
      <header className={`public-topbar landing-topbar ${showFloatingNav ? "" : "landing-topbar--hidden"}`}>
        <div className="landing-topbar-inner">
          <div className="landing-topbar-left">
            <Link to="/" className="brand-inline" aria-label="ClearBadge home">
              <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
            </Link>
            <nav className="public-search-nav" aria-label="Search navigation">
              <Link
                className={`top-link ${location.pathname === "/" ? "active" : ""}`}
                to="/"
                data-reveal
                style={{ "--reveal-delay": "560ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Verify a License
              </Link>
              <Link
                className={`top-link ${location.pathname.startsWith("/find-a-pro") ? "active" : ""}`}
                to="/find-a-pro"
                data-reveal
                style={{ "--reveal-delay": "700ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Find a Pro
              </Link>
              <Link
                className={`top-link ${location.pathname.startsWith("/pricing") ? "active" : ""}`}
                to="/pricing"
                data-reveal
                style={{ "--reveal-delay": "840ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                Pricing
              </Link>
              <Link
                className={`top-link ${location.pathname.startsWith("/about") ? "active" : ""}`}
                to="/about"
                data-reveal
                style={{ "--reveal-delay": "980ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
              >
                About
              </Link>
            </nav>
          </div>
          <div className="public-links landing-topbar-right">
            <Link
              className="top-link"
              to="/login"
              data-reveal
              style={{ "--reveal-delay": "1120ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
            >
              Sign In
            </Link>
            <Link
              className="primary nav-cta mobile-signup"
              to="/login?mode=signup"
              data-reveal
              style={{ "--reveal-delay": "1260ms", "--reveal-x": "-20px", "--reveal-y": "0px" }}
            >
              Get Verified
            </Link>
            <button
              ref={mobileMenuButtonRef}
              className="icon-btn mobile-menu-btn"
              type="button"
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
              aria-controls="about-mobile-menu"
              onClick={() => setMobileMenuOpen((open) => !open)}
            >
              <span className="burger-line" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <div className={`public-mobile-overlay ${mobileMenuOpen ? "open" : ""}`}>
        <button
          type="button"
          className="public-mobile-overlay-dismiss"
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close menu"
        />
        <div
          id="about-mobile-menu"
          ref={mobileDrawerRef}
          className={`public-mobile-drawer ${mobileMenuOpen ? "open" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="public-mobile-header">
            <Link to="/" className="brand-inline" aria-label="ClearBadge home" onClick={() => setMobileMenuOpen(false)}>
              <img src={LOGO_WHITE_PUBLIC_PATH} alt="ClearBadge logo" className="brand-logo" />
            </Link>
            <button className="icon-btn mobile-drawer-close" type="button" aria-label="Close menu" onClick={() => setMobileMenuOpen(false)}>
              <span className="mobile-drawer-close-icon" aria-hidden="true" />
            </button>
          </div>
          <div className="public-mobile-links">
            <Link to="/login" className="public-mobile-link" onClick={() => setMobileMenuOpen(false)}>
              Sign In
            </Link>
            <Link
              to="/login?mode=signup"
              className="public-mobile-link primary"
              onClick={() => setMobileMenuOpen(false)}
            >
              Get Verified
            </Link>
            <div className="mobile-menu-section">
              <span className="mobile-menu-label">TOOLS</span>
              <Link
                to="/"
                className={`public-mobile-link secondary ${isVerifyPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Verify a License</span>
              </Link>
              <Link
                to="/find-a-pro"
                className={`public-mobile-link secondary ${isFindProPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Find a Pro</span>
              </Link>
            </div>
            <div className="mobile-menu-section">
              <span className="mobile-menu-label">RESOURCES</span>
              <Link
                to="/about"
                className={`public-mobile-link secondary ${isAboutPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">About</span>
              </Link>
              <Link
                to="/pricing"
                className={`public-mobile-link secondary ${isPricingPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Pricing</span>
              </Link>
              <Link
                to="/privacy-policy"
                className={`public-mobile-link secondary ${isPrivacyPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Privacy Policy</span>
              </Link>
              <Link
                to="/terms-of-service"
                className={`public-mobile-link secondary ${isTermsPage ? "active" : ""}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="mobile-link-text">Terms of Service</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <main className="landing-main about-main" id="main-content">
        <section className="hero landing-hero about-hero">
          <div className="hero-inner about-hero-inner">
            <div className="about-hero-copy">
              <div className="trust-pill" data-reveal style={{ "--reveal-delay": "1420ms" }}>
                About ClearBadge
              </div>
              <h1 data-reveal style={{ "--reveal-delay": "1580ms" }}>Trust through Transparency.</h1>
              <p className="hero-lead" data-reveal style={{ "--reveal-delay": "1740ms" }}>
                ClearBadge verifies professional licenses so consumers can instantly confirm who they are dealing with.
              </p>
            </div>
            <div className="about-hero-image-wrap" data-reveal style={{ "--reveal-delay": "1900ms" }}>
              <img
                src="/about-1.jpg"
                alt="ClearBadge verification in action"
                className="about-hero-image"
                loading="eager"
              />
            </div>
          </div>
        </section>

        <section className="about-section about-mission how-section built-section">
          <div className="landing-container">
            <div className="built-head">
              <p
                className="eyebrow how-it-works-eyebrow"
                data-reveal
                style={{ "--reveal-delay": "0ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}
              >
                WHY CLEARBADGE<sup>&trade;</sup>
              </p>
              <h2 data-reveal style={{ "--reveal-delay": "140ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                Our <span className="how-title-gradient">Mission</span>
              </h2>
              <p data-reveal style={{ "--reveal-delay": "280ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                We exist to reduce uncertainty in professional interactions by making verified identity and license
                status easy to check before decisions are made.
              </p>
            </div>
            <div className="built-card-grid">
              {MISSION_POINTS.map((point, idx) => (
                <article
                  key={point.title}
                  className="built-card"
                  data-reveal
                  style={{ "--reveal-delay": `${idx * 110}ms` }}
                >
                  <div className="built-card-meta" aria-hidden="true">
                    <span className="built-step-pill">{idx + 1}</span>
                    <span className="built-card-corner-dot" />
                  </div>
                  <div className="built-card-icon">
                    <point.icon className={`step-icon ${point.iconClassName || ""}`.trim()} />
                  </div>
                  <h3>{point.title}</h3>
                  <p className="muted">{point.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="about-section about-context">
          <div className="landing-container">
            <div className="about-context-grid">
              <article className="about-card about-card-main" data-reveal>
                <h2>Why This Matters Right Now</h2>
                <p>
                  AI has made impersonation and credential fraud easier to scale. Consumers increasingly encounter
                  synthetic identities, cloned profiles, and misleading online claims.
                </p>
                <p>
                  ClearBadge gives both sides a practical trust layer: clear proof for legitimate professionals and
                  faster due diligence for consumers before they share money, data, or access.
                </p>
              </article>
              <div className="about-context-side">
                <div className="about-side-image-wrap" data-reveal style={{ "--reveal-delay": "120ms" }}>
                  <img
                    src="/about-2.jpg"
                    alt="Professional using ClearBadge verification"
                    className="about-side-image"
                    loading="lazy"
                  />
                </div>
                <article className="about-card about-highlight" data-reveal style={{ "--reveal-delay": "220ms" }}>
                  <h3>Risk Signals ClearBadge Helps Reduce</h3>
                  <ul>
                    <li>AI-generated profile impersonation</li>
                    <li>Misrepresented licenses or expired credentials</li>
                    <li>Unclear identity in high stakes transactions</li>
                    <li>Manual verification friction that slows decisions</li>
                  </ul>
                </article>
              </div>
            </div>
          </div>
        </section>

        <section className="about-section about-audience">
          <div className="landing-container">
            <div className="built-head">
              <p
                className="eyebrow how-it-works-eyebrow"
                data-reveal
                style={{ "--reveal-delay": "0ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}
              >
                WHO&apos;S CLEARBADGE<sup>&trade;</sup> FOR
              </p>
              <h2 data-reveal style={{ "--reveal-delay": "140ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                Who <span className="how-title-gradient">Benefits</span>
              </h2>
              <p data-reveal style={{ "--reveal-delay": "280ms", "--reveal-x": "-34px", "--reveal-y": "0px" }}>
                ClearBadge is built for both consumers and licensed professionals.
              </p>
            </div>
            <div className="about-audience-grid">
              <article className="about-audience-card core-feature-card" data-reveal>
                <div className="about-audience-media core-feature-media">
                  <div className="about-audience-image-wrap">
                    <img
                      src={consumerImage}
                      alt="Consumer using ClearBadge to verify a professional"
                      className="about-audience-image"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="about-audience-body core-feature-body">
                  <h3>For Consumers</h3>
                  <ul className="about-consumer-list">
                    {CONSUMER_BENEFITS.map((item, itemIdx) => (
                      <li key={item} style={{ "--item-delay": `${160 + itemIdx * 75}ms` }}>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/" className="get-started-primary about-card-link">
                    Verify a license
                  </Link>
                </div>
              </article>
              <article className="about-audience-card core-feature-card" data-reveal style={{ "--reveal-delay": "120ms" }}>
                <div className="about-audience-media core-feature-media">
                  <div className="about-audience-image-wrap">
                    <img
                      src={professionalImage}
                      alt="Licensed professional using ClearBadge to build trust"
                      className="about-audience-image"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className="about-audience-body core-feature-body">
                  <h3>For Licensed Professionals</h3>
                  <ul>
                    {PROFESSIONAL_BENEFITS.map((item, itemIdx) => (
                      <li key={item} style={{ "--item-delay": `${160 + itemIdx * 75}ms` }}>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/login?mode=signup" className="get-started-primary about-card-link">
                    Get verified
                  </Link>
                </div>
              </article>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
}

